/**
 * Link primitive — route-aware wrapper.
 *
 * When `to` prop provided → renders react-router-dom Link.
 * When `href` prop provided (external) → renders plain <a> with target="_blank" rel="noreferrer".
 * Uses transition-base on hover (color transition).
 */
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { cn } from '@/lib/utils';

type BaseProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

export type LinkProps = BaseProps & {
  to?: string;
  href?: string;
  children: ReactNode;
};

const sharedClasses = (className: string | undefined): string =>
  cn(
    'font-sans text-ink underline-offset-4',
    'hover:text-accent',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2',
    'transition-base',
    className,
  );

/**
 * Internal link component using react-router-dom's Link.
 */
function InternalLink({
  to,
  className,
  children,
  ...props
}: BaseProps & { to: string; children: ReactNode }) {
  return (
    <RouterLink to={to} className={sharedClasses(className)} {...props}>
      {children}
    </RouterLink>
  );
}

/**
 * External link component using plain <a>.
 */
const ExternalLink = forwardRef<HTMLAnchorElement, BaseProps & { href: string; children: ReactNode }>(
  function ExternalLink(
    {
      href,
      className,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noreferrer"
        className={sharedClasses(className)}
        {...props}
      >
        {children}
      </a>
    );
  },
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    if ('href' in props && props.href !== undefined) {
      const { to: _to, ...rest } = props;
      return <ExternalLink ref={ref} {...rest} href={props.href} />;
    }
    const { href: _href, ...rest } = props;
    return <InternalLink {...rest} to={props.to as string} />;
  },
);
